import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  alpha,
  useTheme,
} from "@mui/material";
import { useReportInsights } from "../../hooks/useReportData";
import type { AiPanelBlock } from "../../api/types";

interface AiPanelViewProps {
  block: AiPanelBlock;
  clientId: string | null;
  branding?: { primaryColor?: string; chartColor?: string };
  fill?: boolean;
}

const fmt = (value: number | null | undefined, unit: string | null): string => {
  if (value === null || value === undefined) return "–";
  const rounded = Math.round(value * 10) / 10;
  return unit ? `${rounded}${unit}` : `${rounded}`;
};

/**
 * AI-Powered Wellness slide. Shows an overall wellness score ring plus three
 * columns (Key Highlights, Personalised Recommendations, Areas to Watch),
 * conditionally on the block's `show` flags, with a "not medical advice"
 * caption at the foot.
 */
export const AiPanelView: React.FC<AiPanelViewProps> = ({
  block,
  clientId,
  branding,
}) => {
  const theme = useTheme();
  const accent = branding?.primaryColor ?? theme.palette.primary.main;
  const query = useReportInsights(block.filters, clientId);

  if (query.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (query.isError) {
    return <Alert severity="error">Couldn't load AI wellness insights.</Alert>;
  }

  const data = query.data?.data;
  if (!data) {
    return <Alert severity="info">No AI insights for this period.</Alert>;
  }

  const showScore = block.show.score !== false && !!data.overall_wellness_score;
  const showHighlights =
    block.show.highlights !== false && data.key_highlights?.length > 0;
  const showRecs =
    block.show.recommendations !== false &&
    data.personalised_recommendations?.length > 0;
  const showWatch =
    block.show.areasToWatch !== false && data.areas_to_watch?.length > 0;

  const score = Math.round(data.overall_wellness_score?.value ?? 0);

  const columnCount = [showHighlights, showRecs, showWatch].filter(
    Boolean,
  ).length;
  const colSize = columnCount > 0 ? 12 / columnCount : 12;

  return (
    <Box sx={{ width: "100%" }}>
      {block.title && (
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          {block.title}
        </Typography>
      )}

      {showScore && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 3,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <CircularProgress
              variant="determinate"
              value={Math.max(0, Math.min(100, score))}
              size={120}
              thickness={4}
              sx={{ color: accent }}
            />
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 800, color: accent }}>
                {score}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                / 100
              </Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Overall wellness score
            </Typography>
            {data.overall_wellness_score?.calculation_summary_text && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {data.overall_wellness_score.calculation_summary_text}
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <Grid container spacing={2}>
        {showHighlights && (
          <Grid size={{ xs: 12, md: colSize }}>
            <Paper variant="outlined" sx={{ height: "100%", overflow: "hidden" }}>
              <Box sx={{ bgcolor: theme.palette.success.main, px: 2, py: 1 }}>
                <Typography sx={{ color: "#fff", fontWeight: 700 }}>
                  Key Highlights
                </Typography>
              </Box>
              <List dense>
                {data.key_highlights.map((h, i) => (
                  <ListItem key={`${h.title}-${i}`} alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 600 }}>
                          {h.title}: {fmt(h.value, h.unit)}
                          {h.change ? ` (${h.change})` : ""}
                        </Typography>
                      }
                      secondary={h.insight}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        {showRecs && (
          <Grid size={{ xs: 12, md: colSize }}>
            <Paper variant="outlined" sx={{ height: "100%", overflow: "hidden" }}>
              <Box sx={{ bgcolor: theme.palette.info.main, px: 2, py: 1 }}>
                <Typography sx={{ color: "#fff", fontWeight: 700 }}>
                  Personalised Recommendations
                </Typography>
              </Box>
              <List dense>
                {data.personalised_recommendations.map((r, i) => (
                  <ListItem key={`${r.title}-${i}`} alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 600 }}>
                          {r.title}
                        </Typography>
                      }
                      secondary={
                        <>
                          <Typography
                            variant="body2"
                            component="span"
                            sx={{ display: "block", mb: 0.5 }}
                          >
                            {r.rationale}
                          </Typography>
                          {r.actions?.length > 0 && (
                            <Box
                              component="ul"
                              sx={{ m: 0, pl: 2.5, color: "text.secondary" }}
                            >
                              {r.actions.map((a, j) => (
                                <li key={j}>
                                  <Typography variant="body2" component="span">
                                    {a}
                                  </Typography>
                                </li>
                              ))}
                            </Box>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}

        {showWatch && (
          <Grid size={{ xs: 12, md: colSize }}>
            <Paper variant="outlined" sx={{ height: "100%", overflow: "hidden" }}>
              <Box sx={{ bgcolor: theme.palette.warning.main, px: 2, py: 1 }}>
                <Typography sx={{ color: "#fff", fontWeight: 700 }}>
                  Areas to Watch
                </Typography>
              </Box>
              <List dense>
                {data.areas_to_watch.map((w, i) => (
                  <ListItem key={`${w.label}-${i}`} alignItems="flex-start">
                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 600 }}>
                          {w.label}: {fmt(w.value, w.unit)}
                          {w.change ? ` (${w.change})` : ""}
                        </Typography>
                      }
                      secondary={w.reason}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        )}
      </Grid>

      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 2,
          color: "text.secondary",
          fontStyle: "italic",
          borderTop: `1px solid ${alpha(accent, 0.2)}`,
          pt: 1,
        }}
      >
        AI-generated wellbeing insights for informational purposes only — not
        medical advice.
      </Typography>
    </Box>
  );
};
